import { zeroErrorMessage } from "@/lib/zero-error";
import { useState } from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { generateUUID } from "@/lib/utils";
import { toast } from "sonner";

export interface AllergyTag {
  id: string;
  name: string;
  created_at: string;
}

export interface PersonAllergyTag {
  id: string;
  person_id: string;
  allergy_tag_id: string;
  created_at: string;
  allergy_tag?: AllergyTag;
}

type ZeroAllergyTag = {
  id: string;
  name: string;
  createdAt?: number | string | null;
};

type ZeroPersonAllergyTag = {
  id: string;
  personID: string;
  allergyTagID: string;
  createdAt?: number | string | null;
  allergyTag?: ZeroAllergyTag | null;
};

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyTag(row: ZeroAllergyTag): AllergyTag {
  return {
    id: row.id,
    name: row.name,
    created_at: toLegacyTimestamp(row.createdAt),
  };
}

function toLegacyPersonTag(row: ZeroPersonAllergyTag): PersonAllergyTag {
  return {
    id: row.id,
    person_id: row.personID,
    allergy_tag_id: row.allergyTagID,
    created_at: toLegacyTimestamp(row.createdAt),
    allergy_tag: row.allergyTag ? toLegacyTag(row.allergyTag) : undefined,
  };
}

async function reportServerMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

export function useAllergyTags() {
  const [data, details] = useQuery(appQueries.allergyTags.list());
  const tags = ((data ?? []) as ZeroAllergyTag[]).map(toLegacyTag);

  return {
    data: tags,
    isLoading: details.type !== "complete" && tags.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateAllergyTag() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (name: string) => {
    const input = { id: generateUUID(), name };
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.allergyTags.create(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Allergy tag create was rejected",
      );
      return toLegacyTag(input);
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (name: string, options?: MutationOptions<AllergyTag>) => {
      void run(name)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) =>
          toast.error("Failed to create allergy tag: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function usePersonAllergyTags(personId: string | undefined) {
  const query = personId
    ? appQueries.personAllergyTags.byPerson({ personID: personId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!personId });
  const tags = ((data ?? []) as ZeroPersonAllergyTag[]).map(toLegacyPersonTag);

  return {
    data: tags,
    isLoading: !!personId && details.type !== "complete" && tags.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useSetPersonAllergyTags() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    personId,
    tagIds,
  }: {
    personId: string;
    tagIds: string[];
  }) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.personAllergyTags.set({
          personID: personId,
          tagIDs: tagIds,
        }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Person allergy tag update was rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: { personId: string; tagIds: string[] },
      options?: { onSuccess?: () => void },
    ) => {
      void run(input)
        .then(() => options?.onSuccess?.())
        .catch((error) =>
          toast.error("Failed to update allergy tags: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
